import { apiClient } from '../client';
import type { Rock, RockType } from './rocks.api';
import type { Todo } from './todos.api';

/**
 * Client for `/api/eos/issues/*`. Stays in lockstep with
 * `server/src/routes/eos/issues.routes.ts` (Stream B Wave 1, ADR-023).
 */

export type IssueStatus = 'identified' | 'discussing' | 'solved' | 'dropped';
export type IssueTerm = 'short_term' | 'long_term';

export interface Issue {
  id: string;
  organizationId: string;
  title: string;
  description: string | null;
  /** Null = org/leadership list; set = department list. */
  departmentId: string | null;
  status: IssueStatus;
  term: IssueTerm;
  /** Priority within its list — lower = IDS'd first. */
  rank: number;
  raisedByMembershipId: string | null;
  /** Owner display name, included server-side. */
  ownerMembership?: { id: string; user: { firstName: string; lastName: string } } | null;
  ownerMembershipId: string | null;
  resolvedAt: string | null;
  resolutionNotes: string | null;
  solvedInMeetingId: string | null;
  promotedToRockId: string | null;
  promotedToTodoId: string | null;
  archivedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ListIssuesQuery {
  departmentId?: string;
  status?: IssueStatus;
  term?: IssueTerm;
  includeArchived?: boolean;
}

export async function listIssues(q: ListIssuesQuery = {}): Promise<Issue[]> {
  const params = new URLSearchParams();
  if (q.departmentId) params.set('departmentId', q.departmentId);
  if (q.status) params.set('status', q.status);
  if (q.term) params.set('term', q.term);
  if (q.includeArchived) params.set('includeArchived', 'true');
  const query = params.toString() ? `?${params.toString()}` : '';
  const res = await apiClient.get<{ data: Issue[] }>(`/api/eos/issues${query}`);
  return res.data.data;
}

export interface CreateIssueRequest {
  title: string;
  description?: string;
  departmentId?: string;
  term?: IssueTerm;
  ownerMembershipId?: string;
}

export async function createIssue(body: CreateIssueRequest): Promise<Issue> {
  const res = await apiClient.post<{ data: Issue }>('/api/eos/issues', body);
  return res.data.data;
}

export interface UpdateIssueRequest {
  status?: IssueStatus;
  description?: string | null;
  ownerMembershipId?: string | null;
  resolutionNotes?: string | null;
}

export async function updateIssue(id: string, body: UpdateIssueRequest): Promise<Issue> {
  const res = await apiClient.put<{ data: Issue }>(`/api/eos/issues/${encodeURIComponent(id)}`, body);
  return res.data.data;
}

export interface RankIssuesRequest {
  /** Null = the org/leadership list. */
  departmentId: string | null;
  /** Every id must be an OPEN (identified/discussing, unarchived) issue on this list. */
  orderedIds: string[];
}

export async function rankIssues(body: RankIssuesRequest): Promise<{ reordered: number }> {
  const res = await apiClient.put<{ data: { reordered: number } }>('/api/eos/issues/rank', body);
  return res.data.data;
}

export type PromoteIssueRequest =
  | {
      to: 'rock';
      title?: string;
      /** YYYY-MM-DD quarter boundary. */
      quarterStart: string;
      type?: RockType;
      ownerMembershipId?: string;
      departmentId?: string;
      markSolved?: boolean;
    }
  | {
      to: 'todo';
      title?: string;
      ownerMembershipId?: string;
      /** ISO datetime with offset. Defaults to now + 7 days server-side. */
      dueDate?: string;
      markSolved?: boolean;
    };

export interface PromoteIssueResponse {
  issue: Issue;
  /** Created raw (no milestone include) — the `milestones` array is absent. */
  rock: Omit<Rock, 'milestones'> | null;
  todo: Todo | null;
}

export async function promoteIssue(id: string, body: PromoteIssueRequest): Promise<PromoteIssueResponse> {
  const res = await apiClient.post<{ data: PromoteIssueResponse }>(
    `/api/eos/issues/${encodeURIComponent(id)}/promote`,
    body,
  );
  return res.data.data;
}

/** Soft delete — sets `archivedAt`. */
export async function archiveIssue(id: string): Promise<void> {
  await apiClient.delete(`/api/eos/issues/${encodeURIComponent(id)}`);
}
